import { ref } from 'vue'

/**
 * Composable for common form dialog functionality
 * Handles dialog state, form validation, and activator click handling
 */
export function useFormDialog() {
  const dialog = ref(false)
  const loading = ref(false)
  const valid = ref(false)

  /**
   * Handles activator click events to prevent event bubbling issues
   * with Vuetify dialogs inside clickable cards
   */
  const handleActivatorClick = (event: Event, handler?: (e: Event) => void) => {
    event?.preventDefault()
    event?.stopPropagation()
      ; (event as any)?.stopImmediatePropagation?.()
    handler?.(event)
  }

  /**
   * Opens the dialog
   */
  const openDialog = () => {
    dialog.value = true
  }

  /**
   * Closes the dialog and resets loading state
   */
  const closeDialog = () => {
    dialog.value = false
    loading.value = false
  }

  /**
   * Validates a Vuetify form using the async API (Vuetify 3)
   * @param formRef - The form template ref
   * @returns Promise<boolean> - Whether the form is valid
   */
  const validateForm = async (formRef: { validate: () => Promise<{ valid: boolean }> } | undefined): Promise<boolean> => {
    if (!formRef) return false
    const result = await formRef.validate()
    return result.valid
  }

  /**
   * Resets a Vuetify form
   * @param formRef - The form template ref
   */
  const resetForm = (formRef: { reset: () => void } | undefined) => {
    formRef?.reset()
  }

  /**
   * Scrolls an element into view smoothly
   * @param elementRef - The element template ref or element
   */
  const scrollToTop = (elementRef: HTMLElement | { $el?: HTMLElement } | undefined) => {
    if (!elementRef) return
    const el = ('$el' in elementRef ? elementRef.$el : elementRef) as HTMLElement | undefined
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }

  return {
    // State
    dialog,
    loading,
    valid,
    // Methods
    handleActivatorClick,
    openDialog,
    closeDialog,
    validateForm,
    resetForm,
    scrollToTop
  }
}

/**
 * Type for Vuetify form ref
 */
export interface VFormRef {
  validate: () => Promise<{ valid: boolean }>
  reset: () => void
  resetValidation: () => void
}
